/* eslint-disable max-lines */
import type { DefaultError, InfiniteData } from '@tanstack/react-query';
import {
    useInfiniteQuery,
    useQuery,
    useQueryClient,
    useSuspenseInfiniteQuery,
    useSuspenseQuery,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type {
    FetchNextPageOptions,
    InfiniteQueryObserverResult,
    QueryClient,
    QueryFunction,
    QueryKey,
    QueryObserverResult,
    RefetchOptions,
} from '@tanstack/query-core';

import { createCacheKeyBuilder } from './createPaginationEntityCacheHelpers';
import {
    getInfiniteQueryFn,
    getNextPageParam,
    getQueryFn,
} from './createPaginationEntityQueryClientHelpers';
import type {
    CustomQueryPageParams,
    PaginationPageParams,
    QueryPage,
    QueryPageReturnType,
    QueryParamsBaseType,
} from './createPaginationEntityTypes';
import type { CacheKey } from '../utils/internalTypings';
import { createRetryHandler, getRetryOnMountInCaseOfError } from '../utils/retryConfigs';
import { createPlaceholderDataHandler } from '../utils/createPlaceholderDataHandler';

export interface UseEntitiesReturnType<
    Entity,
    QueryParams extends QueryParamsBaseType,
    AdditionalData,
    Error = any,
> {
    /**
     * true when fetching the initial page.
     * */
    isPending: boolean;
    /**
     * true when fetching the next page only.
     * useful when a different spinner is shown
     * compared to the initial fetch
     * */
    isNextPageLoading: boolean;
    /** all data in an array for iterating */
    data?: Entity[];
    /** refetches the current page */
    refetch: () => Promise<Entity[]>;
    /** fetches the next page */
    fetchNextPage: () => Promise<void>;
    error?: Error | null;
    /** important boolean to use to
     * disable the 'fetch next page'
     * mechanism in your feature */
    hasNextPage: boolean;
    isFirstPageLoading: boolean;
    entitiesCount: number;
    nextPageParams: CustomQueryPageParams<QueryParams> | undefined;
    /** metadata that you can optionally
     * set in the payloadCreator */
    additionalData?: AdditionalData;
    didInvalidate: boolean;
    lastUpdated: null | number;
}

export interface UsePaginatedEntityReturnType<Entity, Error = any> {
    data?: Entity;
    isPending: boolean;
    error?: Error | null;
    refetch: () => Promise<Entity | undefined>;
}

export interface UseEntitiesSuspenseReturnType<
    Entity,
    QueryParams extends QueryParamsBaseType,
    AdditionalData,
    Error = any,
> extends Omit<
        UseEntitiesReturnType<Entity, QueryParams, AdditionalData, Error>,
        'isPending' | 'isFirstPageLoading' | 'error'
    > {
    data: Entity[];
    isPreviousPageLoading: boolean;
}

export interface UseEntitySuspenseReturnType<Entity, Error = any>
    extends Omit<UsePaginatedEntityReturnType<Entity, Error>, 'isPending' | 'error'> {
    data: Entity;
}

const usePagination = <Entity, QueryParams extends QueryParamsBaseType, AdditionalData = unknown>({
    data,
    client,
    queryKey,
    refetch: refetchInfiniteQuery,
    fetchNextPage: fetchInfiniteQueryNextPage,
}: {
    data?: InfiniteData<QueryPageReturnType<Entity>, Partial<PaginationPageParams & QueryParams>>;
    client: QueryClient;
    queryKey: QueryKey;
    fetchNextPage: (
        options?: FetchNextPageOptions,
    ) => Promise<
        InfiniteQueryObserverResult<
            InfiniteData<QueryPageReturnType<Entity>, Partial<PaginationPageParams & QueryParams>>,
            Error
        >
    >;
    refetch: (
        options?: RefetchOptions,
    ) => Promise<
        QueryObserverResult<
            InfiniteData<QueryPageReturnType<Entity>, Partial<PaginationPageParams & QueryParams>>,
            Error
        >
    >;
}) => {
    const flatData = useMemo(() => data?.pages?.flatMap((p) => p.data), [data]);

    const entitiesCount = useMemo(() => {
        return data?.pages?.[0]?.totalElements ?? 0;
    }, [data]);

    const nextPageParams: CustomQueryPageParams<QueryParams> | undefined = useMemo(() => {
        if (!data?.pages || data.pages.length === 0) {
            return undefined;
        }

        return getNextPageParam(data?.pages?.[data.pages.length - 1]);
    }, [data]);

    const additionalData: AdditionalData | undefined = useMemo(() => {
        if (!data?.pages || data.pages.length === 0) {
            return undefined;
        }

        return data?.pages[0]?.additionalData as AdditionalData;
    }, [data]);

    const refetch = useCallback(() => {
        client.removeQueries({ queryKey });
        return refetchInfiniteQuery().then((x) => x?.data?.pages?.flatMap?.((p) => p.data) || []);
    }, []);

    const fetchNextPage = useCallback((): Promise<void> => {
        // https://react-query.tanstack.com/reference/useInfiniteQuery
        // if cancelRefetch set to true (which is default), calling fetchNextPage repeatedly will invoke fetchPage every time,
        // whether the previous invocation has resolved or not
        // we want to avoid it, since all other calls are reused when pending
        return fetchInfiniteQueryNextPage({ cancelRefetch: false }).then(() => {});
    }, [fetchInfiniteQueryNextPage]);

    return {
        flatData,
        entitiesCount,
        nextPageParams,
        additionalData,
        refetch,
        fetchNextPage,
    };
};

export const usePaginationEntitiesSuspense = <
    Entity,
    QueryParams extends QueryParamsBaseType,
    AdditionalData = unknown,
>({
    name,
    queryPage,
    params,
    selectId,
    cacheDuration,
    skipParamsHashing,
    skipEntityCacheSync,
}: {
    params: QueryParams;
    name: string;
    enabled?: boolean;
    queryPage: QueryPage<Entity, QueryParams, AdditionalData>;
    cacheDuration: number;
    useErrorBoundary?: boolean;
    cancelable?: boolean;
    skipParamsHashing?: boolean;
    skipEntityCacheSync: boolean;
    selectId: CacheKey<Entity>;
}): UseEntitiesSuspenseReturnType<Entity, QueryParams, AdditionalData> => {
    const client = useQueryClient();

    const queryKey = useMemo(
        () => createCacheKeyBuilder(name).list(skipParamsHashing ? {} : params),
        [name, params, skipParamsHashing],
    );
    const {
        fetchNextPage: fetchInfiniteQueryNextPage,
        data,
        refetch: refetchInfiniteQuery,
        hasNextPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isStale,
        dataUpdatedAt,
    } = useSuspenseInfiniteQuery<
        QueryPageReturnType<Entity, AdditionalData>,
        DefaultError,
        InfiniteData<QueryPageReturnType<Entity>, Partial<PaginationPageParams & QueryParams>>,
        QueryKey,
        Partial<PaginationPageParams & QueryParams>
    >({
        queryKey,
        initialPageParam: {},
        queryFn: getInfiniteQueryFn({
            queryPage,
            skipParamsHashing,
            params,
            name,
            selectId,
            cancelable: false,
            skipEntityCacheSync,
        }) as QueryFunction<
            QueryPageReturnType<Entity, AdditionalData>,
            QueryKey,
            Partial<PaginationPageParams & QueryParams>
        >,
        getNextPageParam,
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false,
        retry: createRetryHandler(false),
        retryOnMount: getRetryOnMountInCaseOfError(queryKey),
        gcTime: Infinity,
        staleTime: cacheDuration,
    });

    const { flatData, refetch, entitiesCount, additionalData, fetchNextPage, nextPageParams } =
        usePagination<Entity, QueryParams, AdditionalData>({
            data,
            client,
            fetchNextPage: fetchInfiniteQueryNextPage,
            refetch: refetchInfiniteQuery,
            queryKey,
        });

    return {
        data: flatData ?? [],
        refetch,
        entitiesCount,
        additionalData,
        hasNextPage: hasNextPage ?? false,
        isPreviousPageLoading: isFetchingPreviousPage,
        isNextPageLoading: isFetchingNextPage,
        nextPageParams,
        fetchNextPage,
        didInvalidate: isStale,
        lastUpdated: dataUpdatedAt,
    };
};

export const usePaginationEntities = <
    Entity,
    QueryParams extends QueryParamsBaseType,
    AdditionalData = unknown,
>({
    name,
    queryPage,
    enabled = true,
    params,
    selectId,
    cacheDuration,
    skipParamsHashing,
    cancelable = false,
    skipEntityCacheSync,
    refetchInterval,
}: {
    params: QueryParams;
    name: string;
    enabled?: boolean;
    queryPage: QueryPage<Entity, QueryParams, AdditionalData>;
    cacheDuration: number;
    useErrorBoundary?: boolean;
    cancelable?: boolean;
    skipParamsHashing?: boolean;
    skipEntityCacheSync: boolean;
    selectId: CacheKey<Entity>;
    refetchInterval?: number;
}): UseEntitiesReturnType<Entity, QueryParams, AdditionalData> => {
    const client = useQueryClient();

    const queryKey = useMemo(
        () => createCacheKeyBuilder(name).list(skipParamsHashing ? {} : params),
        [name, params, skipParamsHashing],
    );
    const {
        fetchNextPage: fetchInfiniteQueryNextPage,
        isFetching,
        isLoading,
        data,
        error,
        refetch: refetchInfiniteQuery,
        hasNextPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isStale,
        dataUpdatedAt,
    } = useInfiniteQuery<
        QueryPageReturnType<Entity, AdditionalData>,
        DefaultError,
        InfiniteData<QueryPageReturnType<Entity>, Partial<PaginationPageParams & QueryParams>>,
        QueryKey,
        Partial<PaginationPageParams & QueryParams>
    >({
        queryKey,
        initialPageParam: {},
        queryFn: getInfiniteQueryFn({
            queryPage,
            skipParamsHashing,
            params,
            name,
            cancelable,
            selectId,
            skipEntityCacheSync,
        }) as QueryFunction<
            QueryPageReturnType<Entity, AdditionalData>,
            QueryKey,
            Partial<PaginationPageParams & QueryParams>
        >,
        getNextPageParam,
        refetchOnWindowFocus: false,
        refetchInterval,
        refetchIntervalInBackground: false,
        // without retry true when cancelable, query is immediately canceled and not re-fetched on quick mount/remount in dev mode
        retry: createRetryHandler(cancelable),
        retryOnMount: getRetryOnMountInCaseOfError(queryKey),
        gcTime: Infinity,
        staleTime: cacheDuration,
        placeholderData: createPlaceholderDataHandler(queryKey),
        enabled,
        throwOnError: false,
    });

    const { flatData, refetch, entitiesCount, additionalData, fetchNextPage, nextPageParams } =
        usePagination<Entity, QueryParams, AdditionalData>({
            data,
            client,
            fetchNextPage: fetchInfiniteQueryNextPage,
            refetch: refetchInfiniteQuery,
            queryKey,
        });

    const isPending = enabled ? isFetching || isLoading : isFetching;

    return {
        isPending,
        data: flatData,
        refetch,
        error,
        entitiesCount,
        additionalData,
        hasNextPage: hasNextPage ?? false,
        isFirstPageLoading: isPending && !isFetchingNextPage && !isFetchingPreviousPage,
        isNextPageLoading: isFetchingNextPage,
        nextPageParams,
        fetchNextPage,
        didInvalidate: isStale,
        lastUpdated: dataUpdatedAt,
    };
};

function useEntity<Entity>({
    refetch,
}: {
    refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<Entity>>;
}) {
    const refetchCallback: () => Promise<Entity | undefined> = useCallback(() => {
        return refetch().then(({ data }) => data);
    }, [refetch]);
    return { refetch: refetchCallback };
}

const NOOP = async (): Promise<never> => {
    throw new Error('Not implemented');
};

export const usePaginationEntity = <Entity, QueryByIdParams extends QueryParamsBaseType>({
    id,
    params,
    enabled = true,
    name,
    fetch,
    cacheDuration,
    selectId,
    localListsSynchronisation,
    cancelable = false,
    skipEntityCacheSync,
    refetchInterval,
}: {
    id?: string;
    params?: QueryByIdParams;
    name: string;
    enabled?: boolean;
    fetch: (id: string, params?: QueryByIdParams, signal?: AbortSignal) => Promise<Entity>;
    cacheDuration: number;
    useErrorBoundary?: boolean;
    localListsSynchronisation: boolean | undefined;
    cancelable?: boolean;
    selectId: CacheKey<Entity>;
    skipEntityCacheSync: boolean;
    refetchInterval?: number;
}): UsePaginatedEntityReturnType<Entity> => {
    const queryKey = useMemo(() => createCacheKeyBuilder(name).detail(id ?? ''), [name, id]);

    const {
        data: entityDetail,
        isFetching,
        isLoading,
        error: entityError,
        refetch,
    } = useQuery({
        queryKey,
        refetchOnWindowFocus: false,
        refetchInterval,
        refetchIntervalInBackground: false,
        // without retry true when cancelable, query is immediately canceled and not re-fetched on quick mount/remount in dev mode
        retry: createRetryHandler(cancelable),
        retryOnMount: getRetryOnMountInCaseOfError(queryKey),
        gcTime: Infinity,
        staleTime: cacheDuration,
        enabled: !!id && enabled,
        queryFn: id
            ? getQueryFn({
                  queryById: fetch,
                  params,
                  id,
                  name,
                  cancelable,
                  selectId,
                  localListsSynchronisation,
                  skipEntityCacheSync,
              })
            : NOOP,
        throwOnError: false,
    });
    const { refetch: refetchCallback } = useEntity({ refetch });

    if (!id) {
        return {
            isPending: false,
            error: null,
            refetch: () => Promise.resolve(undefined),
            data: undefined,
        };
    }

    const isPending = enabled ? isFetching || isLoading : isFetching;

    return {
        data: entityDetail ?? undefined,
        isPending,
        error: entityError,
        refetch: refetchCallback,
    };
};

type UsePaginationEntitySuspenseOptions<QueryByIdParams extends QueryParamsBaseType, Entity> = {
    params?: QueryByIdParams;
    name: string;
    enabled?: boolean;
    fetch: (id: string, params?: QueryByIdParams, signal?: AbortSignal) => Promise<Entity>;
    cacheDuration: number;
    useErrorBoundary?: boolean;
    localListsSynchronisation: boolean | undefined;
    cancelable?: boolean;
    selectId: CacheKey<Entity>;
    skipEntityCacheSync: boolean;
};

export function usePaginationEntitySuspense<Entity, QueryByIdParams extends QueryParamsBaseType>(
    id: string,
    options: UsePaginationEntitySuspenseOptions<QueryByIdParams, Entity>,
): UseEntitySuspenseReturnType<Entity>;

export function usePaginationEntitySuspense<Entity, QueryByIdParams extends QueryParamsBaseType>(
    id: string | undefined,
    options: UsePaginationEntitySuspenseOptions<QueryByIdParams, Entity>,
): UseEntitySuspenseReturnType<Entity | undefined>;

export function usePaginationEntitySuspense<Entity, QueryByIdParams extends QueryParamsBaseType>(
    id: string | undefined,
    {
        params,
        name,
        fetch,
        cacheDuration,
        selectId,
        localListsSynchronisation,
        skipEntityCacheSync,
    }: UsePaginationEntitySuspenseOptions<QueryByIdParams, Entity>,
) {
    const queryKey = useMemo(() => createCacheKeyBuilder(name).detail(id ?? ''), [name, id]);

    const { data: entityDetail, refetch } = useSuspenseQuery({
        queryKey,
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false,
        retry: createRetryHandler(false),
        retryOnMount: getRetryOnMountInCaseOfError(queryKey),
        gcTime: Infinity,
        staleTime: cacheDuration,
        queryFn: id
            ? getQueryFn({
                  queryById: fetch,
                  params,
                  id,
                  name,
                  cancelable: false,
                  selectId,
                  localListsSynchronisation,
                  skipEntityCacheSync,
              })
            : NOOP,
    });
    const { refetch: refetchCallback } = useEntity({ refetch });

    if (!id) {
        return {
            refetch: () => Promise.resolve(undefined),
            data: undefined,
        };
    }

    return {
        data: entityDetail,
        refetch: refetchCallback,
    };
}
